/*
 * Copyright © 2024 Himitsu Lab Limited. All Rights Reserved.
 */

import { Menu, Transition } from "@headlessui/react"
import { Fragment } from "react"
import Button from "../../Components/base/button/button"
import Icon from "../../Components/base/icon/icon"
import NewIcon from "../../Components/base/icon/newIcons"
import { ToolTip } from "../../Components/base/tooltip/tooltip"
import { useMeetingRemainderHooks } from "../../Hooks/AttendingHooks"
import { Meeting } from "../../models/meeting.model"
import { MeetingNotificationList } from "../../models/meetingNotficationList.model"
import { MeetingNotificationRemainder } from "../../models/meetingNotificationRemainder.model"
import  CloseIcon  from '../../Assets/icons/beeMG-icons-close.svg'

const style = {
  reminderIcon: `min-w-rightIcon flex flex-col items-center justify-center`,
}

/**
 * AttendingEventsRemainder renders a dropdown of reminder options for a meeting event.
 * The dropdown is only visible if the user has already set reminders for the event.
 * The dropdown is also only visible if the user has permissions to set reminders for the event.
 * The dropdown contains a list of all reminder times that the user can set for the event.
 * The dropdown also contains a list of all reminder times that the user has already set for the event.
 * The user can click on a reminder time to add it to the list of reminders for the event.
 * The user can click on a reminder time that has already been set to remove it from the list of reminders for the event.
 * @param meeting - the meeting object that the reminders are being set for
 * @returns a JSX element that renders the dropdown of reminder options
 */
export const AttendingEventsRemainder = ({ meeting }: { meeting: Meeting }) => {

  const { remainders,
    remainderSuccess,
    meetingRemainderSuccess,
    remList,
    addNewRemainder,
    removeRemainder
  } = useMeetingRemainderHooks({ meeting })

  if (!remainderSuccess && !meetingRemainderSuccess) {
    return <></>
  }

  if ((remainders === undefined || remainders.length <= 0) && (remList === undefined || remList.length <= 0)) {
    return <></>
  }

  return (
    <span data-testid="add_remainder" id={`btn_remainder${meeting.id}`} className={style.reminderIcon} onClick={e => e.stopPropagation()} >
      <Menu as="div" className="inline-block text-left">
        <div>
          <Menu.Button className="inline-flex w-full justify-center">
            <ToolTip tip="setRemainder" keyId="setRemainder">
              <i className="z-30 cursor-pointer">
                {((remainders && remainders.length > 0) && (remList && remList.length >= 0)) ? <NewIcon size="small" icon="BEEMG_LOGO_ICON_NOTIFICATION_SELECTED"/> :
                  <NewIcon size="small" icon="BEEMG_LOGO_ICON_NOTIFICATION"/>}
              </i>
            </ToolTip>
          </Menu.Button>
        </div>
        <Transition
          as={Fragment}
          enter="transition ease-out duration-100"
          enterFrom="transform opacity-0 scale-95"
          enterTo="transform opacity-100 scale-100"
          leave="transition ease-in duration-75"
          leaveFrom="transform opacity-100 scale-100"
          leaveTo="transform opacity-0 scale-95"
        >
          <Menu.Items className="z-40 absolute mt-2 py-2 px-2 origin-top-right w-50 divide-y divide-gray-100 rounded-md bg-white ring-1 ring-black ring-opacity-5 focus:outline-none max-h-40 overflow-auto translate-x-5">
            {remainders && remainders?.map((remainder: MeetingNotificationRemainder, index: number) => (
              <div className="flex items-center gap-1 p-0.5 bg-BeeMG-yellow rounded-md m-1" key={'div' + remainder.id}>
                <Button
                  id={`btn_removeRemainder${index}`}
                  key={remainder.id}
                  data-testid={`btn_removeRemainder${index}`}
                  size="sm"
                  color="userCategory"
                  className="flex-grow p-0.5 bg-BeeMG-yellow rounded-md"
                  onClick={() => removeRemainder(remainder.id)}
                >
                  <span>{remainder.meetingNotificationList.notificationTimingInDays}</span>
                </Button>
                <span
                  className="flex items-center justify-center p-1 cursor-pointer"
                  onClick={() => removeRemainder(remainder.id)}
                >
                  <img src={CloseIcon} alt="close" width={15} height={15} />
                </span>
              </div>           
            ))}
            <div className=" mt-2 px-1 py-1 ">
              {remList?.map((data: MeetingNotificationList, index: number) => (
                <Menu.Item key={data.id}>
                  {({ active }) => (
                    <>
                      <button
                        id={`btn_addRemainder${index}`}
                        data-testid={`btn_addRemainder${index}`}
                        className={`${active ? 'bg-primary w-full text-black' : 'text-gray-900'
                          }  group flex rounded-md px-6 py-1 text-sm`}
                        onClick={e => addNewRemainder(data)}
                      >
                        {data.notificationTimingInDays}
                      </button>
                    </>
                  )}
                </Menu.Item>
              ))}
            </div>
          </Menu.Items>
        </Transition>
      </Menu>
    </span>
  )
}